<template>
  <div
    class="key"
    :class="{
      'key--dark': mode === 'dark'
    }"
    data-testid="hidden-key"
  >
    <div
      class="key__content"
      :class="{
        'key__content--bordered': showMsg
      }"
    >
      <div
        :id="`key-value-${visibleTrailer}`"
        class="key__value"
        data-testid="key-value"
      >
        <template v-if="show">
          {{ value }}
        </template>

        <template v-else>
          {{ keyToRender }}
          <button
            v-if="value"
            type="button"
            :class="{
              'show-key-button': true,
            }"
            data-testid="show-key-button"
            @click="showKey"
            @keypress="showKey"
          >
            <template v-if="show">
              {{ $t('manage.organization.apiKeys.hideKey') }}
            </template>
            <template v-else>
              {{ $t('manage.organization.apiKeys.showKey') }}
            </template>
          </button>
        </template>
      </div>

      <BTooltip
        v-if="showMsg"
        :target="`key-value-${trailer}`"
        placement="bottom"
        show
      >
        {{ $t('manage.organization.apiKeys.saveKeyTooltip') }}
      </BTooltip>

      <button
        v-if="value"
        v-b-tooltip.top="{
          title: tooltip
        }"
        type="button"
        class="key__copy-button"
        data-testid="copy-to-clipboard-button"
        @click="copy(value)"
        @keypress="copy(value)"
      >
        <elIcon
          name="clipboard"
        />
      </button>
    </div>
  </div>
</template>

<script>
import { useClipboard } from '@ui/composables/useClipboard'
import { useTimeout } from '@ui/composables/useTimeout'
import { BTN_COPIED_TIMEOUT } from 'Modules/constants'
import { VBTooltip, BTooltip } from '@ui/components'
import elIcon from './elIcon.vue'
const NUMBER_OF_DIGITS_TO_SHOW = 4

const VALID_MODES = [
  'light',
  'dark'
]

const SHOW_KEY_TIMEOUT = 300000 // 5 min

export default {
  components: {
    elIcon,
    BTooltip
  },
  directives: {
    'b-tooltip': VBTooltip
  },
  props: {
    value: {
      type: String,
      default: ''
    },
    trailer: {
      type: String,
      default: ''
    },
    mode: {
      type: String,
      default: 'light',
      validator(val) {
        return !val || VALID_MODES.includes(val)
      }
    },
    keyLength: {
      type: Number,
      default: 32
    },
    replicable: {
      type: Boolean,
      default: true
    }
  },
  setup() {
    const { copy, copied } = useClipboard({ copiedMessageDuration: BTN_COPIED_TIMEOUT })
    return {
      copy,
      copied
    }
  },
  data() {
    return {
      show: false
    }
  },
  computed: {
    visibleTrailer() {
      return this.trailer || this.value.substr(this.value.length - NUMBER_OF_DIGITS_TO_SHOW)
    },
    placeholderLength() {
      return this.keyLength - NUMBER_OF_DIGITS_TO_SHOW
    },
    keyToRender() {
      return `${' \u2022 '.repeat(this.placeholderLength)}${this.visibleTrailer}`
    },
    tooltip() {
      if (this.copied) {
        return this.$t('manage.organization.apiKeys.copiedToClipboard')
      }
      return this.$t('manage.organization.apiKeys.copyToClipboard')
    },
    showMsg() {
      return !this.replicable && !!this.value
    }
  },
  methods: {
    showKey() {
      this.toggleKeyVisibility(true)
      useTimeout(() => {
        this.toggleKeyVisibility(false)
      }, SHOW_KEY_TIMEOUT)
    },
    toggleKeyVisibility(value) {
      this.show = value
    }
  }
}
</script>

<style lang="scss" scoped>
@import '~@ui/styles/src/scss/utils/lib';

.key {
  $this: &;

  display: flex;

  &__content {
    display: flex;
    align-items: baseline;

    &--bordered {
      border: 1px solid $orange-40;
    }
  }

  &__value {
    position: relative;
    min-width: 237px;
    padding: 2px 5px;
    text-align: center;
    background: $grey-85;
    border: 1px solid $grey-75;

    #{$this}--dark & {
      @include background-opacity($grey-85, 0.2);
      @include border-opacity($grey-75, 0.2);
    }

    &:hover {
      .show-key-button {
        opacity: 0.9;
        transition: 0.1s linear opacity;
      }
    }
  }

  &__copy-button {
    padding: 2px 5px;
    color: $grey-40;
    background: $grey-75;
    border: 1px solid $grey-65;
    border-radius: 0;

    #{$this}--dark & {
      color: #aaa;

      @include background-opacity($grey-75, 0.3);
      @include border-opacity($grey-65, 0.3);
    }

    &:hover,
    &:active,
    &:focus {
      outline: none;
      box-shadow: none;
    }
  }
}

.show-key-button {
  @extend %flex-centered;

  position: absolute;
  top: 0;
  bottom: 0;
  left: 0;
  width: 100%;
  color: $darkPrimaryColor;
  background: $grey-70;
  border: none;
  opacity: 0;
  transition: 0.1s linear opacity;

  .key--dark & {
    background: $grey-50;

    @include background-opacity($grey-35, 0.8);
  }
}
</style>
